import { Anchor, Heading } from '@backend/lib-design-system';
import Image from 'next/image';
import { type FC, useMemo } from 'react';
import { styled } from 'styled-components';

import { Column } from '~/components/Column';
import { Container } from '~/components/Container/Container';
import { ListGridLayout } from '~/components/ListGridLayout/ListGridLayout';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import { useListsContext } from '~/contexts/ListsContext/ListsContext';
import { useTranslation } from '~/hooks';
import { useHref } from '~/hooks/useHref';
import { getMediaQuery } from '~/utils/responsive/media';
import { sanitize } from '~/utils/sanitize';

interface HomePageOffersProps {
  totalJobOffers?: number;
  isSearchApplied?: boolean;
}

const HomePageOffersContainer = styled(Container)`
  background-color: #efefef;
`;

const MainTitle = styled(Heading)`
  text-align: center;
  > span {
    color: #4040b4;
  }
`;

const ListsRow = styled.div<{ $isSearchApplied?: boolean }>`
  display: flex;
  gap: 32px;
  flex-direction: column;

  ${getMediaQuery('laptop', 'desktop')} {
    ${({ $isSearchApplied }) => ($isSearchApplied ? 'flex-direction: row;' : undefined)}
  }
`;

const ListAndImageContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: stretch;
  gap: 32px;

  &:first-child {
    flex-direction: row-reverse;
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  /* the illustration keeps its proportions whatever the height of the list beside it */
  img {
    object-fit: contain;
  }

  ${getMediaQuery('mobile', 'tablet')} {
    display: none;
  }
`;

const StyledListGridLayout = styled.div`
  flex: 1;
  width: 50%;
`;

const ViewAllLink = styled(Anchor)`
  display: inline-flex;
  align-items: center;
  color: #4040b4 !important;
  font-family: 'Red Hat Display', sans-serif;
  font-weight: 500;
  line-height: 1.5;
`;

const MAX_ITEMS = 10;

const homePageOffersTypes = ['cities', 'sectors'] as const;

type HomePageOffersType = (typeof homePageOffersTypes)[number];

export const HomePageOffers: FC<HomePageOffersProps> = ({ totalJobOffers = 0, isSearchApplied }) => {
  const t = useTranslation();
  const { getPageHref } = useHref();
  const { language } = useBusinessUnitContext();
  const { cityList, sectorList } = useListsContext();

  const listGridItems: Record<HomePageOffersType, string[]> = useMemo(
    () => ({
      cities: cityList.slice(0, MAX_ITEMS),
      sectors: sectorList.slice(0, MAX_ITEMS),
    }),
    [cityList, sectorList],
  );

  const formattedCount = useMemo(
    () => new Intl.NumberFormat(language).format(totalJobOffers),
    [totalJobOffers, language],
  );

  return (
    <HomePageOffersContainer>
      <Column>
        <MainTitle
          variant="heading-2"
          dangerouslySetInnerHTML={{
            __html: sanitize(t('home_page_offers.title', { count: totalJobOffers, formattedCount })),
          }}
        />
        <ListsRow $isSearchApplied={isSearchApplied}>
          {homePageOffersTypes.map(type => (
            <ListAndImageContainer key={type}>
              {!isSearchApplied && (
                <ImageContainer>
                  <Image
                    src={`/images/home-page-offers/layers/${type}.png`}
                    alt=""
                    fill
                    // hidden below the laptop breakpoint (1024px), half of the page's content width above it
                    sizes="(max-width: 1024px) 1px, 560px"
                  />
                </ImageContainer>
              )}
              <StyledListGridLayout>
                <ListGridLayout
                  type={type}
                  maxColumns={1}
                  list={listGridItems[type]}
                  footer={
                    <ViewAllLink href={getPageHref(type)}>{t(`home_page_offers.buttons.${type}`)} &gt;</ViewAllLink>
                  }
                />
              </StyledListGridLayout>
            </ListAndImageContainer>
          ))}
        </ListsRow>
      </Column>
    </HomePageOffersContainer>
  );
};
